import React, { useCallback } from "react";
import { Check } from "lucide-react";
import { getStageIcon, getStageAccent, isOwnerAssignedToStage, toggleOwnerStage } from "./utils";

/**
 * A single workflow-stage row.
 */
export default function StageRow({ stage, owners, setOwners, accentIndex }) {
  const StageIcon = getStageIcon(stage.name);
  const accent = getStageAccent(accentIndex);

  const handleToggleStage = useCallback(
    (userId, stageId) => {
      setOwners((prevOwners) =>
        toggleOwnerStage(prevOwners, userId, stageId)
      );
    },
    []
  );

  return (
    <tr className="group border-b border-slate-100 last:border-b-0 transition-all duration-150 hover:bg-slate-50/40">
      <td className="whitespace-nowrap px-6 py-3.5 align-middle">
        <div className="flex items-center gap-3">
          <div
            className={`flex h-9 w-9 flex-none items-center justify-center rounded-lg ${accent.bg || 'bg-slate-100'} transition-transform duration-200 group-hover:scale-105`}
          >
            <StageIcon className={`h-4.5 w-4.5 ${accent.text || 'text-slate-600'}`} aria-hidden="true" />
          </div>
          <div>
            <p className="font-sans text-sm font-semibold text-slate-800 tracking-tight">
              {stage.name}
            </p>
            <p className="font-sans text-xs text-slate-400 font-medium">
              Score: {stage.score}
            </p>
          </div>
        </div>
      </td>

      {owners.map((owner) => (
        <OwnerCheckboxCell
          key={owner.user.id}
          owner={owner}
          stage={stage}
          onToggle={handleToggleStage}
        />
      ))}
    </tr>
  );
}

function OwnerCheckboxCell({ owner, stage, onToggle }) {
  const checked = isOwnerAssignedToStage(owner, stage.id);

  const handleChange = useCallback(() => {
    onToggle(owner.user.id, stage.id);
  }, [onToggle, owner.user.id, stage.id]);

  return (
    <td className="px-4 py-3.5 text-center align-middle">
      <label className="inline-flex cursor-pointer items-center justify-center p-1 rounded-md transition hover:bg-slate-100/50">
        <input
          type="checkbox"
          className="peer sr-only"
          checked={checked}
          onChange={handleChange}
          aria-label={`Assign ${owner.user.name} to ${stage.name}`}
        />
        <span
          className={`relative flex h-5 w-5 items-center justify-center rounded-md border transition-all duration-200 peer-focus-visible:ring-2 peer-focus-visible:ring-primary peer-focus-visible:ring-offset-2 ${checked
            ? "border-primary bg-primary text-white"
            : "border-slate-300 bg-white shadow-sm group-hover:border-slate-400"
            }`}
        >
          {checked && <Check className="h-3 w-3 stroke-[3]" aria-hidden="true" />}
        </span>
      </label>
    </td>
  );
}